import { Button, CustomTooltip } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ChatbotService, ChatbotTrainingStatus } from 'api-clients/monolith';
import classnames from 'classnames';
import React, { useCallback, useMemo, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { addMessageAction } from 'containers/FlashMessage/actions';

import { SelectedBrand } from '../types';
import { ErrorIcon } from './icons/ErrorIcon';
import { LoadingIcon } from './icons/LoadingIcon';
import { PendingIcon } from './icons/PendingIcon';
import { SuccessIcon } from './icons/SuccessIcon';
import { messages } from './messages';

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: '5px',
    width: '182px',
    padding: theme.spacing(1, 2),
    '&:disabled': {
      opacity: 'unset',
      backgroundColor: 'unset',
    },
    pointerEvents: 'auto',
    marginBottom: '5px',
  },
  icon: {
    width: '16px',
    height: '16px',
    marginLeft: theme.spacing(1),
  },
  buttonText: {
    lineHeight: '20px',
    fontWeight: 450,
  },
  '@keyframes rotation': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(359deg)',
    },
  },
  loader: {
    animation: '$rotation 0.7s infinite linear',
  },
}));

export interface TrainingStatusProps {
  selectedBrand: SelectedBrand;
  trainingStatus: ChatbotTrainingStatus;
  setTrainingStatus: React.Dispatch<
    React.SetStateAction<ChatbotTrainingStatus>
  >;
}

export const TrainingStatus: VFC<TrainingStatusProps> = ({
  selectedBrand,
  trainingStatus,
  setTrainingStatus,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const statusDetails = useMemo(() => {
    switch (trainingStatus) {
      case 'SUCCESS':
        return {
          message: messages.successStatus,
          icon: <SuccessIcon className={styles.icon} />,
        };
      case 'PENDING':
      case 'RUNNING':
        return {
          message: messages.pendingStatus,
          icon: <PendingIcon className={styles.icon} />,
        };
      case 'FAILURE':
        return {
          message: messages.errorStatus,
          icon: <ErrorIcon className={styles.icon} />,
        };
      default:
        return null;
    }
  }, [trainingStatus, styles.icon]);

  const [isFetchingStatus, setIsFetchingStatus] = useState(false);

  const fetchTrainingStatus = useCallback(() => {
    return ChatbotService.postInternalApiChatbotSettingsRefreshKnowledgeBaseStatus(
      selectedBrand.brandId,
      selectedBrand.isAllBrands,
    );
  }, [selectedBrand]);

  const handleFetchStatusClick = () => {
    if (trainingStatus === 'PENDING' || trainingStatus === 'RUNNING') {
      setIsFetchingStatus(true);
      fetchTrainingStatus()
        .then(result => {
          setTrainingStatus(result.faq_knowledge_base_status);
          if (
            result.faq_knowledge_base_status === 'PENDING' ||
            result.faq_knowledge_base_status === 'RUNNING'
          ) {
            dispatch(
              addMessageAction(
                intl.formatMessage(messages.fetchPendingStatus),
                'success',
              ),
            );
          }
        })
        .catch(() => {
          dispatch(
            addMessageAction(
              intl.formatMessage(messages.fetchErrorStatus),
              'error',
            ),
          );
        })
        .finally(() => {
          setIsFetchingStatus(false);
        });
    }
  };

  if (!trainingStatus) return null;
  if (!statusDetails) return null;

  return (
    <CustomTooltip
      title={
        (trainingStatus === 'PENDING' || trainingStatus === 'RUNNING') &&
        intl.formatMessage(messages.tooltip)
      }
      dense
      placement="top"
    >
      <Button
        type="secondary"
        className={styles.button}
        onClick={handleFetchStatusClick}
        disabled={trainingStatus === 'SUCCESS' || trainingStatus === 'FAILURE'}
      >
        <Typography variant="body2" className={styles.buttonText}>
          <FormattedMessage {...statusDetails.message} />
        </Typography>
        {isFetchingStatus ? (
          <LoadingIcon className={classnames(styles.icon, styles.loader)} />
        ) : (
          statusDetails.icon
        )}
      </Button>
    </CustomTooltip>
  );
};
